import React, { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminBehaviorService, {
  BehaviorRange,
  FunnelDefinition,
  FunnelResult,
} from '../../../services/api/adminBehaviorService';
import { formatMs, formatNumber, formatPercent } from './format';

interface Props {
  range: BehaviorRange;
}

/**
 * config/behavior.php のファネル定義を選び、ステップごとの到達数・転換率・所要時間を表示する。
 */
const FunnelsTab: React.FC<Props> = ({ range }) => {
  const [definitions, setDefinitions] = useState<FunnelDefinition[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [result, setResult] = useState<FunnelResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminBehaviorService
      .getFunnels()
      .then((list) => {
        if (cancelled) return;
        setDefinitions(list);
        setSelected((current) => current || list[0]?.key || '');
      })
      .catch(() => {
        if (!cancelled) setError('ファネル定義の取得に失敗しました');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminBehaviorService
      .getFunnel(selected, range)
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch(() => {
        if (!cancelled) setError('ファネルの取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, range]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <label htmlFor="behavior-funnel-select" className="text-sm text-dark">
          ファネル
        </label>
        <TaskySelect
          id="behavior-funnel-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="sm:w-96"
        >
          {definitions.map((definition) => (
            <option key={definition.key} value={definition.key}>
              {definition.label}
            </option>
          ))}
        </TaskySelect>
        {result && (
          <span className="text-xs text-warm-text-muted">
            {result.actor === 'user' ? 'ユーザー単位' : '端末単位'} / 猶予 {result.window_days} 日
          </span>
        )}
      </div>

      {error && <TaskyErrorState title={error} />}
      {!error && loading && (
        <div className="flex justify-center py-12">
          <TaskySpinner />
        </div>
      )}
      {!error && !loading && result && result.steps[0]?.count === 0 && (
        <TaskyEmptyState title="この期間に最初のステップへ到達したユーザーがいません" />
      )}
      {!error && !loading && result && result.steps[0]?.count > 0 && (
        <>
          <div className="h-64 rounded-tasky-md border border-warm-border p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={result.steps} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" name="到達" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <TaskyTable className="min-w-[720px]">
              <TaskyTableHead>
                <TaskyTableRow>
                  <TaskyTableHeaderCell>ステップ</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>到達</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>転換率（前比）</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>離脱率</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>次まで 中央値</TaskyTableHeaderCell>
                  <TaskyTableHeaderCell>次まで p90</TaskyTableHeaderCell>
                </TaskyTableRow>
              </TaskyTableHead>
              <TaskyTableBody>
                {result.steps.map((step, index) => (
                  <TaskyTableRow key={`${index}-${step.label}`} data-testid={`funnel-step-${index}`}>
                    <TaskyTableCell>
                      <span className="mr-2 text-warm-text-muted">{index + 1}.</span>
                      {step.label}
                    </TaskyTableCell>
                    <TaskyTableCell>{formatNumber(step.count)}</TaskyTableCell>
                    <TaskyTableCell>{formatPercent(step.conversion_rate)}</TaskyTableCell>
                    <TaskyTableCell className={step.drop_off_rate >= 50 ? 'text-destructive' : ''}>
                      {index === 0 ? '-' : formatPercent(step.drop_off_rate)}
                    </TaskyTableCell>
                    <TaskyTableCell>{formatMs(step.median_ms_to_next)}</TaskyTableCell>
                    <TaskyTableCell>{formatMs(step.p90_ms_to_next)}</TaskyTableCell>
                  </TaskyTableRow>
                ))}
              </TaskyTableBody>
            </TaskyTable>
          </div>
        </>
      )}
    </div>
  );
};

export default FunnelsTab;
